import { User } from "@shuttlerock/auth-types";
import type { FeatureGate } from "@shuttlerock/feature-gates";
import { allowAllFeatureGate, getFeatureGate } from "@shuttlerock/feature-gates";
import { captureException } from "@shuttlerock/observability";
import { GraphQLClient } from "graphql-request";
import { data as dataResponse } from "react-router";
import {
  ClientAccountQuery as ClientAccountQueryResult,
  ClientAccountQueryVariables,
  UserAccountsQuery as UserAccountsQueryResult,
  UserAccountsQueryVariables,
} from "./graphql/generated/graphql";
import {
  ClientAccountDocument,
  UserAccountsDocument,
} from "./graphql/queries/client-account.graphql";
import { singleton } from "./sharescope";
import {
  ClientAccountContext,
  ClientBrand,
  ClientAccount,
  DefaultClientAccount,
  AccountType,
} from "./types";

const cache = singleton("client-account-context/cache", new Map<User, ClientAccountContext>());

export async function getUserClientAccount(
  gqlClient: GraphQLClient,
  user: User | undefined,
  featureGate?: FeatureGate,
): Promise<ClientAccountContext> {
  if (!user) {
    return { isMultiAccountUser: false, isMultiBrandUser: false };
  }

  const gate = featureGate ?? getFeatureGate();

  const isAccessControlBypass = gate === allowAllFeatureGate;

  if (!isAccessControlBypass && gate.has("accounts.multiTenant")) {
    return {
      isMultiAccountUser: true,
      isMultiBrandUser: true,
    };
  }

  const cachedContext = cache.get(user);
  if (cachedContext) {
    return cachedContext;
  }

  // Prevent the cache from growing indefinitely, we only care about the most recent token.
  cache.clear();

  const {
    defaultAccount: accessDefaultAccount,
    defaultBrand: accessDefaultBrand,
    isMultiAccountUser,
    isMultiBrandUser,
    error,
  } = await userAccountsQuery(gqlClient);
  if (error) {
    throw dataResponse({ error: "Failed to fetch client accounts for user" }, 500);
  }

  const baseDefaultAccount = accessDefaultAccount;
  const baseDefaultBrand = accessDefaultBrand;

  const defaultAccount = await getDefaultAccount(gqlClient, baseDefaultAccount);

  const context = {
    isMultiAccountUser,
    isMultiBrandUser,
    defaultAccount,
    defaultBrand: baseDefaultBrand,
  };

  cache.set(user, context);
  return context;
}

type UserClientsQueryResult = {
  defaultAccount?: ClientAccount;
  defaultBrand?: ClientBrand;
  isMultiAccountUser: boolean;
  isMultiBrandUser: boolean;
  error: unknown | null;
};

const resolveResourceAttributes = (
  attrs?: Record<string, unknown> | null,
): Record<string, unknown> => {
  if (!attrs) return {};
  const resolved: Record<string, unknown> = {};
  Object.entries(attrs).forEach(([key, value]) => {
    if (value && typeof value === "object" && "effective" in value) {
      resolved[key] = (value as { effective?: unknown }).effective ?? null;
      return;
    }
    resolved[key] = value;
  });
  return resolved;
};

async function userAccountsQuery(gqlClient: GraphQLClient): Promise<UserClientsQueryResult> {
  try {
    const data = await gqlClient.request<UserAccountsQueryResult, UserAccountsQueryVariables>(
      UserAccountsDocument,
    );

    const access = data.me?.organizationalUnitAccess;
    type OrgAccess = NonNullable<
      NonNullable<UserAccountsQueryResult["me"]>["organizationalUnitAccess"]
    >;

    const mapAccount = (ou?: OrgAccess["defaultRootOrgUnit"]): ClientAccount | undefined => {
      if (!ou) return undefined;
      const attrs = resolveResourceAttributes(
        ou.resourceAttributes as Record<string, unknown> | null | undefined,
      );
      return {
        // name is nullable on the graph: null means WITHHELD, not "no name".
        // The context carries a display string, so a withheld name renders as
        // empty rather than propagating null through every consumer.
        key: ou.key,
        resourceKey: ou.resourceKey ?? undefined,
        organizationalUnitKey: ou.key,
        accountType: parseAccountType(attrs.account_type),
        name: ou.name ?? "",
        type: ou.resourceType as unknown as ClientAccount["type"],
      };
    };

    const mapBrand = (ou?: OrgAccess["defaultLeafOrgUnit"]): ClientBrand | undefined => {
      if (!ou) return undefined;
      return {
        key: ou.key,
        resourceKey: ou.resourceKey ?? undefined,
        organizationalUnitKey: ou.key,
        name: ou.name ?? "",
        type: ou.resourceType as unknown as ClientBrand["type"],
      };
    };
    return {
      defaultAccount: mapAccount(access?.defaultRootOrgUnit),
      defaultBrand: mapBrand(access?.defaultLeafOrgUnit),
      isMultiAccountUser: access?.hasMultipleRootUnits ?? false,
      isMultiBrandUser: access?.hasMultipleLeafUnits ?? false,
      error: null,
    };
  } catch (error) {
    captureException(error);
    return {
      isMultiAccountUser: false,
      isMultiBrandUser: false,
      error,
    };
  }

  function parseAccountType(raw?: unknown): ClientAccount["accountType"] {
    switch (raw) {
      case "subscription":
        return AccountType.Subscription;
      case "license":
        return AccountType.License;
      case "license_plus":
        return AccountType.LicensePlus;
      case "unrestricted":
      default:
        return AccountType.Unrestricted;
    }
  }
}

async function getDefaultAccount(
  gqlClient: GraphQLClient,
  clientAccount?: ClientAccount,
): Promise<DefaultClientAccount | undefined> {
  if (!clientAccount) {
    return undefined;
  }

  const data = await gqlClient.request<ClientAccountQueryResult, ClientAccountQueryVariables>(
    ClientAccountDocument,
    {
      key: clientAccount.key,
    },
  );

  return {
    ...clientAccount,
    isPlatformAiEnabled: data.clientAccount?.enablePlatformAI ?? false,
  };
}
